import { useState, useRef, useEffect, useCallback } from 'preact/compat';
import type { CSSProperties } from 'preact';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';
import ActionFactories from '../lib/Actions';
import { CuepointReachedEvent } from 'src/events';
import { getCoordsInElement, isPointInCircle } from 'src/lib/util';
import Overlay from './Overlay';
import { Controls } from './Controls';
import {
  Actions,
  BoundActions,
  Cuepoint,
  DataRetrievalStrategy,
  LiveData,
  Publication,
  WidgetConfig,
} from 'src/types';
import { ProcessingStrategy } from 'src/lib/ProcessingStrategy';
import {
  CLASSNAME_PREFIX,
  DEFAULT_WIDGET_STYLE,
  HEY_GENIUS_FIELD,
  HOST_CLASSNAME,
} from 'src/constants';
import Avatar from './Avatar';
import { RoughAnnotation } from 'rough-notation/lib/model';
import { GlobalContextProvider } from 'src/globalContext';
import useCurrentBreakpoint from 'src/hooks/useCurrentBreakpoint';
import { Bubble } from './Bubble';
import { analytics } from 'src/services/analytics';
import InjectedStyles from 'src/lib/InjectedStyles';
import { PauseIcon } from './PauseIcon';
import { PlayIcon } from './PlayIcon';
import NotationHighlight from '../lib/Highlight';
import { CircularMediaProgress } from './CircularMediaProgress';
import { useVideoAnalytics } from '@libertify.frontend/analytics';

type RefObject = {
  media?: HTMLAudioElement | HTMLVideoElement | null;
  visualizer: {
    play: () => void;
    pause: () => void;
    stop: () => void;
    duration: number;
  };
};

// Helper function to create bound actions from config
function createBoundActions(
  actions: Actions,
  entities: Record<string, () => unknown>,
): BoundActions {
  const boundActions: BoundActions = {};
  Object.entries(actions).forEach(([phase, actionList]) => {
    actionList.forEach((action) => {
      const factory = ActionFactories[action][0];
      const params = ActionFactories[action][1];
      let args;
      if (params) {
        args = entities[params]();
      }
      if (boundActions[phase]) {
        boundActions[phase].push(factory(args));
      } else {
        boundActions[phase] = [factory(args)];
      }
    });
  });
  return boundActions;
}

// Helper function to remove event listeners
function removeEventListeners(
  container: HTMLElement | null | undefined,
  actions: BoundActions,
) {
  Object.entries(actions).forEach(([phase, actionList]) => {
    actionList.forEach((action) => {
      container?.removeEventListener(phase, action);
    });
  });
}

// Helper to check if media has ended and handle cleanup
function handleMediaEnd(
  currentTime: number,
  duration: number,
  playedTillTheEnd: React.MutableRefObject<boolean>,
  stopAndRewind: () => void,
  mediaSrc: string,
  nextCuepoint: React.MutableRefObject<number>,
): boolean {
  const isMediaEnded = currentTime >= duration;
  if (isMediaEnded && !playedTillTheEnd.current) {
    playedTillTheEnd.current = true;
    setTimeout(() => stopAndRewind(), 1000);
    NotationHighlight.clearShownHighlights();
    analytics().logEvent('media_stop', {
      media_url: mediaSrc,
      media_time: currentTime,
      media_duration: duration,
      highlight_index: nextCuepoint.current,
    });
    return true;
  }
  return false;
}

// Helper to set position for first cuepoint
function setFirstCuepointPosition(
  nextCuepoint: number,
  config: WidgetConfig | null,
  height: string | number | undefined,
  setPosition: (
    fn: (pos: { x: number; y: number; clickedInCircle: boolean }) => {
      x: number;
      y: number;
      clickedInCircle: boolean;
    },
  ) => void,
) {
  if (
    nextCuepoint === 0 &&
    config?.features?.['scroll_to_bottom_at_start'] !== false
  ) {
    const maxBottomPositionIncreaser = window.innerWidth <= 768 ? 2 : 3;
    setPosition((position) => ({
      ...position,
      x: window.innerWidth <= 768 ? -43 : -20,
      y:
        window.innerHeight -
        parseInt(String(height ?? '0')) -
        100 * maxBottomPositionIncreaser,
    }));
  }
}

// Helper to log highlight analytics
function logHighlightAnalytics(
  highlightDetails: {
    highlight?: { content?: { text?: string | null } };
    element?: { tagName?: string };
    position?: { x?: number; y?: number };
  },
  loopCount: number,
  hlsLength: number | undefined,
  currentTime: number,
  nextCuepoint: number,
) {
  if (loopCount !== hlsLength) {
    const eventParams = {
      media_time: currentTime,
      highlight_text: highlightDetails.highlight?.content?.text?.slice(0, 50),
      highlight_index: nextCuepoint,
      highlight_element: highlightDetails.element?.tagName,
      highlight_position_x: highlightDetails.position?.x,
      highlight_position_y: highlightDetails.position?.y,
    };
    analytics().logEvent('widget_highlight', eventParams);
  }
}

// Helper to get button color
function getButtonColor(config: WidgetConfig | null): string {
  return config?.colors?.play_button ?? config?.colors?.primary ?? '#DB0581';
}

// Component for visualizer pause buttons (playing state)
function VisualizerPauseButtons({
  currentBreakpoint,
  config,
}: Readonly<{
  currentBreakpoint: string;
  config: WidgetConfig | null;
}>) {
  const color = getButtonColor(config);
  return (
    <>
      <div
        style={{
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: -2,
          left: 0,
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          transform: 'opacity 1s ease-in-out',
          opacity: 0.8,
        }}
      >
        <PauseIcon
          size={currentBreakpoint === 'small' ? 16 : 21}
          color={color}
        />
      </div>
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: -2,
          left: 0,
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          transform: 'opacity 1s ease-in-out',
        }}
      >
        <PauseIcon
          size={currentBreakpoint === 'small' ? 19 : 27}
          color={color}
          style={{ opacity: 0.6, filter: 'blur(5px)' }}
        />
      </div>
    </>
  );
}

// Component for visualizer play buttons (paused state)
function VisualizerPlayButtons({
  currentBreakpoint,
  config,
}: Readonly<{
  currentBreakpoint: string;
  config: WidgetConfig | null;
}>) {
  const color = getButtonColor(config);
  return (
    <>
      <div
        style={{
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: -2,
          left: 2,
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          transform: 'opacity 1s ease-in-out',
        }}
      >
        <PlayIcon
          size={
            config?.styles?.play_button?.[currentBreakpoint] ??
            (currentBreakpoint === 'small' ? 13 : 18)
          }
          color={color}
        />
      </div>
      <div
        style={{
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: -2,
          left: 2,
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          transform: 'opacity 1s ease-in-out',
        }}
      >
        <PlayIcon
          size={currentBreakpoint === 'small' ? 17 : 25}
          color={color}
          style={{
            opacity: 0.6,
            filter: 'blur(5px)',
          }}
        />
      </div>
    </>
  );
}

// Component for text play button
function TextPlayButton({
  config,
  currentBreakpoint,
  hover,
  playButtonColor,
}: Readonly<{
  config: WidgetConfig | null;
  currentBreakpoint: string;
  hover: boolean;
  playButtonColor: string;
}>) {
  const color = getButtonColor(config);
  return (
    <div
      style={{
        cursor: 'pointer',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: -2,
        left: 2,
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        transform: 'opacity 1s ease-in-out',
      }}
    >
      <div
        style={{
          backgroundColor: config?.colors?.play_button_bg ?? 'black',
          opacity: hover ? 1 : 0.85,
          padding: '3px 9px',
          display: 'flex',
          borderRadius: 50,
          position: 'absolute',
          bottom: 14,
          color: playButtonColor,
          fontWeight: 'bold',
          fontFamily: 'sans-serif',
          fontSize: 10,
          gap: 3,
          border: `1px solid ${playButtonColor}`,
          transition: '250ms',
          alignItems: 'center',
        }}
      >
        <PlayIcon
          size={
            config?.styles?.play_button?.[currentBreakpoint] ??
            (currentBreakpoint === 'small' ? 9 : 12)
          }
          color={color}
        />
        {config?.components?.play_button?.[config.language]}
      </div>
    </div>
  );
}

// Component for play/pause button overlay
function PlayButtonOverlay({
  isPlaying,
  config,
  currentBreakpoint,
  hover,
  playButtonColor,
}: Readonly<{
  isPlaying: boolean;
  config: WidgetConfig | null;
  currentBreakpoint: string;
  hover: boolean;
  playButtonColor: string;
}>) {
  if (isPlaying && config?.widget_type === 'visualizer') {
    return (
      <VisualizerPauseButtons
        currentBreakpoint={currentBreakpoint}
        config={config}
      />
    );
  }

  if (!isPlaying && config?.widget_type === 'visualizer') {
    return (
      <VisualizerPlayButtons
        currentBreakpoint={currentBreakpoint}
        config={config}
      />
    );
  }

  if (!isPlaying && config?.components?.play_button) {
    return (
      <TextPlayButton
        config={config}
        currentBreakpoint={currentBreakpoint}
        hover={hover}
        playButtonColor={playButtonColor}
      />
    );
  }

  return null;
}

export default function Widget<
  T extends {
    highlights?: NotationHighlight[];
    annotations?: { [key: string]: RoughAnnotation[] };
    cuepoints?: Cuepoint[];
  },
>({
  dataRetrievalStrategy,
  processingStrategy,
}: Readonly<{
  dataRetrievalStrategy: DataRetrievalStrategy;
  processingStrategy: ProcessingStrategy<T>;
}>) {
  const avatarRef = useRef<RefObject | null>(null);
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mediaSrc, setMediaSrc] = useState('');
  const [displayedCurrentTime, setDisplayedCurrentTime] = useState(0);
  const [publication, setPublication] = useState<Publication | undefined>();

  const { handleUnload, setupPlayerTracking } = useVideoAnalytics({
    projectType: 'hey_genius',
    projectId: publication?.project_id,
    publicationId: publication?.instance_id,
  });
  // Needed so that we can access it from rAF
  const mediaContext = useRef<{ currentTime: number; isPlaying: boolean }>({
    currentTime: 0,
    isPlaying: false,
  });
  const annotations = useRef<T | object>({});
  const actionsRef = useRef<BoundActions>();
  const nextCuepoint = useRef(0);
  const ticking = useRef(false);

  const [position, setPosition] = useState<{
    x: number;
    y: number;
    clickedInCircle: boolean;
  }>({ x: 0, y: 0, clickedInCircle: false });

  // callback on avatar initialization
  const avatarRefCallback = useCallback((ref) => {
    avatarRef.current = ref;
    if (!animationFrameId.current) {
      animationFrameId.current = animate();
    }
    if (
      ref.media &&
      (ref.media instanceof HTMLVideoElement ||
        ref.media instanceof HTMLAudioElement)
    ) {
      // setup player tracking each time the media changes
      setupPlayerTracking(ref.media);
    }
  }, []);

  function updateCurrentTime(currentTime: number, delta: number) {
    if (mediaContext.current.isPlaying) {
      currentTime += delta / 1000;
      mediaContext.current.currentTime = currentTime;
      if (window?.[HEY_GENIUS_FIELD]?.debug) {
        setDisplayedCurrentTime(currentTime);
      }
    }
  }
  function stopAndRewind() {
    setIsPlaying(false);
    if (mediaContext.current.currentTime === 0) return;
    mediaContext.current.currentTime = 0;
    const media = avatarRef.current?.media;
    if (media) {
      media.currentTime = 0;
      media.pause();
    }
    const player = avatarRef.current?.visualizer;
    if (player) {
      player?.stop();
    }
    hostContainer?.dispatchEvent(new Event('END_OF_MEDIA'));
    prevTimestamp.current = 0;
    nextCuepoint.current = 0; // replay highlights when replaying media
    animationFrameId.current = 0;
    cancelAnimationFrame(animationFrameId.current);
  }

  // Fetch highlights data and media data
  const highlights = useRef<NotationHighlight[] | undefined>(undefined);
  const entities = {
    // we have to evaluate those lazily
    highlights: () => highlights.current,
  };
  const hostContainer = window.document.querySelector<HTMLElement>(
    '.' + HOST_CLASSNAME,
  );
  const cuepoints = useRef<unknown[] | undefined>([]);
  const animationFrameId = useRef(0);
  const wasPlaying = useRef(false);
  const prevTimestamp = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playedTillTheEnd = useRef(false);
  const { currentTime } = mediaContext.current;
  const [hover, setHover] = useState(false);

  let loopCount = 0;
  // Main loop of the widget that handles the cuepoints,
  // tracks the current time and reacts to it
  const mainLoop = useCallback(
    (timestamp: number) => {
      const { currentTime } = mediaContext.current;
      const duration =
        avatarRef.current?.media?.duration ??
        avatarRef.current?.visualizer?.duration;
      if (!prevTimestamp.current) {
        prevTimestamp.current = timestamp;
      }
      if (duration) {
        const delta = timestamp - prevTimestamp.current;
        const shouldReturn = handleMediaEnd(
          currentTime,
          duration,
          playedTillTheEnd,
          stopAndRewind,
          mediaSrc,
          nextCuepoint,
        );
        if (shouldReturn) return;
        updateCurrentTime(currentTime, delta);
      }
      const hls = highlights?.current;
      const cuepointStartsAtZero = hls?.[nextCuepoint.current]?.time === 0;
      const cuepointTime = hls?.[nextCuepoint.current]?.time ?? 0;
      if (
        (hls && currentTime >= cuepointTime && cuepointTime !== 0) ||
        (cuepointStartsAtZero && mediaContext.current.isPlaying)
      ) {
        document.dispatchEvent(
          CuepointReachedEvent(hls?.[nextCuepoint.current].id),
        );

        setFirstCuepointPosition(
          nextCuepoint.current,
          config,
          height,
          setPosition,
        );

        const highlightDetails = hls?.[nextCuepoint.current].highlightDetails;
        const prevHighlightDetails =
          hls?.[nextCuepoint.current - 1]?.highlightDetails;
        if (
          highlightDetails &&
          (nextCuepoint.current === 0 ||
            highlightDetails.highlight.original_id !==
              prevHighlightDetails?.highlight.original_id)
        ) {
          logHighlightAnalytics(
            highlightDetails,
            loopCount,
            hls?.length,
            currentTime,
            nextCuepoint.current,
          );
        }

        nextCuepoint.current += 1;
        loopCount += 1;
      }
      prevTimestamp.current = timestamp;

      return requestAnimationFrame(mainLoop);
    },
    [config],
  );
  const animate = () => {
    // Start polling when the component mounts
    return requestAnimationFrame(mainLoop);
  };

  //Handle page unload for analytics
  useEffect(() => {
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  useEffect(() => {
    mediaContext.current.isPlaying = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    (async () => {
      try {
        const liveData = (window[HEY_GENIUS_FIELD] as { liveData?: LiveData })
          ?.liveData;
        const data = await dataRetrievalStrategy.retrieveData(liveData);
        if (data === null) {
          // Publication isn't ready yet
          return;
        }
        const {
          config,
          highlights: retrievedHighlights,
          cuepoints: cuepointsData,
        } = data;
        const cps = cuepointsData;
        const hls = retrievedHighlights;
        if (data.publication) {
          setPublication(data.publication);
        }

        const processingResult = processingStrategy.process(
          hls,
          config,
          // DLY
          // casting from AdapterCuepoint to Cuepoint to pass type check to processingStrategy
          // but it's not perfectly correct
          // TODO improve typing
          cps as Cuepoint[],
          {
            scrollHandler: scrollHandler.current,
            isEditor: window[HEY_GENIUS_FIELD]?.is_editor ?? false,
          },
        );

        if ('highlights' in processingResult) {
          highlights.current = processingResult.highlights;
        } else if ('annotations' in processingResult) {
          annotations.current = processingResult.annotations as {
            [key: string]: RoughAnnotation[];
          };
          highlights.current = processingResult.highlights;
        }

        // Create actions
        const actions: Actions = config?.actions ?? {};
        actionsRef.current = createBoundActions(actions, entities);
        cuepoints.current = cuepointsData;
        setConfig(config);
        const pageVideo = dataRetrievalStrategy.getMedia();
        setMediaSrc(pageVideo);

        // Inject styles
        const style = document.createElement('style');
        if (config?.debug) {
          style.innerHTML = InjectedStyles({ zIndex: 9999 });
        } else if (config?.styles?.max_zindex) {
          // One less than the widget z-index
          style.innerHTML = InjectedStyles({
            zIndex: config?.styles?.max_zindex - 1,
          });
        }

        if (hostContainer && config.styles?.max_zindex) {
          // update ASAP zindex to avoid widget flickering on top of modal e.g.
          hostContainer.style.zIndex = String(config.styles?.max_zindex);
        }
        document.head.appendChild(style);
      } catch (e) {
        // We could display something to the user, but that would require
        // additional code and styling
        console.debug(e);
      } finally {
        // We could display an error message here
        setLoading(false);
      }
    })();
  }, []);

  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollHandler = useRef<() => void>(() => {
    return;
  });

  const [showBubble, setShowBubble] = useState(false);
  //to show the bubble only once
  const [bubbleShowed, setBubbleShowed] = useState(false);

  useEffect(() => {
    // Pause the video on visibility change
    function visibilityChangeHandler() {
      const isTabActive =
        window.document.visibilityState === 'visible' &&
        window.document.hidden === false;
      if (isTabActive) {
        if (wasPlaying.current) {
          avatarRef.current?.media?.play();
          avatarRef.current?.visualizer?.play();
          setIsPlaying(true);
        }
      } else {
        wasPlaying.current = isPlaying;
        avatarRef.current?.media?.pause();
        avatarRef.current?.visualizer?.pause();
        setIsPlaying(false);
      }
    }
    window.document.addEventListener(
      'visibilitychange',
      visibilityChangeHandler,
    );
    return () => {
      window.document.removeEventListener(
        'visibilitychange',
        visibilityChangeHandler,
      );
    };
  }, [isPlaying]);

  // Add event listeners for actions
  useEffect(() => {
    if (loading) return;
    const acts = actionsRef.current;
    if (!acts) return;
    Object.entries(acts).forEach(([phase, actions]) => {
      actions.forEach((action) => {
        hostContainer?.addEventListener(phase, action);
      });
    });
    return () => {
      removeEventListeners(hostContainer, acts);
    };
  }, [loading]);

  useEffect(() => {
    window.document.addEventListener('scroll', handleScrolling);
    return () => {
      window.document.removeEventListener('scroll', handleScrolling);
    };
  }, [config]); //to update the config when it is ready

  const visibleDelay = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showBubbleTimeout = useRef<ReturnType<typeof setTimeout>>();
  const currentBreakpoint = useCurrentBreakpoint(config);
  const { height } = config?.styles?.widget?.[currentBreakpoint] ?? {
    height: '0',
  };
  const defaultDimensions = {
    height: '80px',
    width: '80px',
    start_x: -20,
    start_y: 130,
    min_y: 130,
  };
  const currentBreakpointStyle = config?.styles?.widget?.[currentBreakpoint];
  useEffect(() => {
    if (loading || !config?.styles?.widget) return;
    // Set the widget into the top right corner based on the configured dimensions
    const hostContainer = window.document.querySelector<HTMLElement>(
      '.' + HOST_CLASSNAME,
    );

    let { width, height } = currentBreakpointStyle ?? defaultDimensions;
    const { start_x, start_y } = currentBreakpointStyle ?? defaultDimensions;
    if (typeof width === 'number') {
      width = `${width}px`;
    }
    if (typeof height === 'number') {
      height = `${height}px`;
    }
    if (hostContainer) {
      hostContainer.style.width = width ?? defaultDimensions.width;
      hostContainer.style.height = height ?? defaultDimensions.height;

      hostContainer.style.top = '0';
      hostContainer.style.right = '0';

      if (currentBreakpoint === 'small') {
        handleShowBubble();
      }
      setPosition((position) => ({
        ...position,
        x: start_x ?? defaultDimensions.start_x,
        y: start_y ?? defaultDimensions.start_y,
      }));
    }
  }, [config?.styles?.widget, currentBreakpoint, loading]);

  const haveScrolledPastMinY = useRef(false);
  const handleScrolling = useCallback(() => {
    const scrollPercentage =
      window.scrollY / (window.document.body.scrollHeight - window.innerHeight);

    const scrollSpeedFactor = 1.5;
    let y = window.innerHeight * scrollPercentage * scrollSpeedFactor;
    let { min_y: minY = 0 } = currentBreakpointStyle ?? defaultDimensions;
    const { start_y: startY = 0 } = config?.styles?.widget ?? defaultDimensions;
    if (startY < minY && !haveScrolledPastMinY.current) {
      minY = startY;
    }

    // Do not let the widget go off the screen and lower than footer
    const parsedHeight = parseInt(height as string);
    if (y + parsedHeight > window.innerHeight) {
      y = window.innerHeight - parsedHeight;
    }
    if (y > minY) {
      haveScrolledPastMinY.current = true;
    }
    const maxYFromBottom =
      window.innerHeight - (currentBreakpointStyle?.max_y_from_bottom ?? 0);
    if (y < (minY ?? 0)) {
      setPosition((position) => ({
        ...position,
        y: minY ?? 0,
      }));
    } else if (y > maxYFromBottom) {
      setPosition((position) => ({
        ...position,
        y: maxYFromBottom,
      }));
    } else {
      setPosition((position) => ({ ...position, y }));
    }
  }, [config]);

  const animationHandler = () => {
    ticking.current = false;
    if (!highlights.current) return;
    for (const highlight of highlights.current) {
      highlight.move(highlight.counter ?? 0); // Recalculate the position of the highlights
    }
  };

  const resizeHandler = useCallback(() => {
    if (!ticking.current) {
      ticking.current = true;
      requestAnimationFrame(animationHandler);
    }
  }, [config]);

  scrollHandler.current = resizeHandler;

  useEffect(() => {
    const handler = resizeHandler;
    window.addEventListener('resize', handler);
    window.document.addEventListener('scroll', handler, {
      passive: true,
      capture: true,
    });
    return () => {
      window.removeEventListener('resize', handler);
      window.document.removeEventListener('scroll', handler);
    };
  }, [resizeHandler]);

  useEffect(() => {
    if (avatarRef.current?.media) {
      animationFrameId.current = animate();
    }

    return () => {
      return cancelAnimationFrame(animationFrameId.current);
    };
  }, [config]);

  // Show bubble on scroll on desktop
  useEffect(() => {
    const showBubbleOnScroll = () => {
      if (currentBreakpoint !== 'small' && !bubbleShowed) {
        handleShowBubble();
      }
    };
    window.document.addEventListener('scroll', showBubbleOnScroll, {
      once: true,
    });
    return () => {
      window.document.removeEventListener('scroll', showBubbleOnScroll);
    };
  }, [bubbleShowed]);

  useEffect(() => {
    if (config) {
      analytics().init({
        shouldTrack: config.analytics_enabled ?? false,
        eventParams: {
          organization_id: config.organization_id,
          product_sku: config.product_sku,
          language: config.language,
          app_id: config.app_id,
        },
      });
      analytics().logEvent('widget_view');
    }
  }, [config]);

  function togglePlay() {
    handleHideBubble(true);
    setBubbleShowed(true);

    const media = avatarRef.current?.media ?? avatarRef.current?.visualizer;
    const visualizer = avatarRef.current?.visualizer;
    const currentTime = mediaContext.current?.currentTime;
    playedTillTheEnd.current = false;
    if (!animationFrameId.current) {
      animationFrameId.current = animate();
    }
    if (isPlaying) {
      media?.pause?.();
      visualizer?.pause?.();
      setVisible(true);
      setIsPlaying(false);
      analytics().logEvent('media_pause', {
        media_url: mediaSrc,
        media_time: currentTime,
        media_duration: media?.duration,
        highlight_index: nextCuepoint.current,
      });
    } else {
      hideOverlay();
      media?.play?.();
      visualizer?.play?.();
      setIsPlaying(true);
      analytics().logEvent('media_play', {
        media_url: mediaSrc,
        media_time: currentTime,
        media_duration: media?.duration,
        highlight_index: nextCuepoint.current,
      });
    }
  }

  const showOverlay = (delay = 500) => {
    visibleDelay.current = setTimeout(() => {
      setVisible(true);
    }, delay);
  };

  const hideOverlay = () => {
    if (visibleDelay.current) {
      clearTimeout(visibleDelay.current);
    }
    setVisible(false);
  };

  const handleShowBubble = () => {
    if (bubbleShowed) return;

    const delay = currentBreakpoint === 'small' ? 3000 : 0;

    // Set the timeout for showing the bubble
    showBubbleTimeout.current = setTimeout(() => {
      setShowBubble(true);
      setTimeout(() => {
        setShowBubble(false);
      }, 5000);
    }, delay);
  };

  const dismissBubble = () => {
    setShowBubble(false);
  };

  const handleHideBubble = (dismiss = false) => {
    setShowBubble(false);
    // if playing already video for instance
    if (dismiss) dismissBubble();

    // Clear the timeouts to prevent the bubble from showing
    if (showBubbleTimeout.current) {
      clearTimeout(showBubbleTimeout.current);
      showBubbleTimeout.current = undefined;
    }
  };

  function handleDragStop(e: DraggableEvent, data: DraggableData) {
    e.stopPropagation();
    if (
      Math.abs(data.x - position.x) < Math.abs(5) &&
      Math.abs(data.y - position.y) < Math.abs(5) &&
      position.clickedInCircle
    ) {
      togglePlay();
    }
    try {
      analytics().logEvent('widget_drag', {
        position_x: data.x,
        position_y: data.y,
        position_origin_x: position.x,
        position_origin_y: position.y,
        media_time: currentTime,
        highlight_index: nextCuepoint.current,
      });
    } catch (e) {
      console.debug(e);
    }
    setPosition({ ...position, x: data.x, y: data.y });
  }
  function handleDragStart(e: DraggableEvent, data: DraggableData) {
    const container = containerRef.current;
    if (!container) return;
    const { x, y } = getCoordsInElement(container, e);
    const Px = x;
    const Py = y;
    const radius = parseInt(
      config?.styles?.widget?.[currentBreakpoint]?.width ?? '100',
    ); // Circle radius
    const Cx = radius; // Circle center X
    const Cy = radius; // Circle center Y

    const isInside = isPointInCircle(Px, Py, Cx, Cy, radius);
    setPosition({ x: data.x, y: data.y, clickedInCircle: isInside });
  }

  const handlePoweredByClick = () => {
    analytics().logEvent('widget_poweredby_click', {
      media_time: currentTime,
      highlight_index: nextCuepoint.current,
    });
  };
  const playButtonHeight = currentBreakpoint === 'small' ? '10px' : '18px';
  const playButtonWidth = currentBreakpoint === 'small' ? '5px' : '9px';
  const isVisualizer = config?.widget_type === 'visualizer';
  const playButtonColor =
    config?.colors?.play_button ?? config?.colors?.primary ?? '#000000';
  const css = `
  .${CLASSNAME_PREFIX}hl-id {
    display: none;
  }
  .widget-wrapper {
    opacity: 0;
    ${config?.styles?.widget?.font_family && `font-family: ${config?.styles?.widget?.font_family}, sans-serif;`}
  }
  .widget-wrapper.ready {
    opacity: 1;
    transition: opacity 1s;
  }
  .widget-container {
    user-select: none;
  }
  canvas {
    border-radius: 50%
  }
  .powered-by {
    display: block;
    text-decoration: none;
    text-align: center;
    z-index: 22;
    font-size: ${config?.components?.powered_by?.[currentBreakpoint]?.fontSize};
    color: #606060;
    position: absolute;
    bottom: -2em;
  // background: #ffffff88;
  // background: linear-gradient(to bottom, rgba(256,256,256,0) 0%, rgba(256,256,256,1) 100%);
  // padding: 1px 3px;
  // border-radius: 10px;
  }
  .debug {
    position: absolute;
    color: red;
    font-size: 14px;
    top: 0;
    right: 0;
  }

  .playpause label {
    pointer-events: none;
    display: block;
    box-sizing: border-box;
    width: 0;
    height: ${playButtonHeight};
    border-color: transparent transparent transparent ${playButtonColor};
    transition: 100ms all ease ${isVisualizer ? ';' : ', opacity 1ms none;'}
    cursor: pointer;
    border-style: double;
    border-width: 0px 0 0px ${playButtonHeight};
    opacity: ${isVisualizer ? 1 : 0}
  }
  .playpause input[type="checkbox"] {
    position: absolute;
    left: -9999px;
  }
  .playpause input[type="checkbox"]:checked + label {
    border-style: solid;
    opacity: 1;
    border-width: ${playButtonWidth} 0 ${playButtonWidth} ${playButtonHeight};
  }
`;

  const contextValue = { config };

  const progressBarEnabled = config?.components?.progress_bar?.enabled;

  const seekTo = useCallback((t: number) => {
    const media = avatarRef.current?.media ?? null;
    const viz = avatarRef.current?.visualizer ?? null;

    const duration = media?.duration ?? viz?.duration ?? 0;

    const target = Math.max(
      0,
      Math.min(t, Number.isFinite(duration) ? duration : t),
    );

    // Seek the *actual* player first
    if (media && Number.isFinite(media.duration)) {
      media.currentTime = target;
    }

    // Keep the in-memory cursor in sync
    mediaContext.current.currentTime = target;
  }, []);

  return (
    <GlobalContextProvider value={contextValue}>
      <style>
        {css}
        {config?.styles?.root_styles}
      </style>
      <Draggable
        position={position}
        onStart={handleDragStart}
        onStop={handleDragStop}
      >
        <div
          className={`widget-wrapper ${!loading ? 'ready' : ''}`}
          style={{
            ...DEFAULT_WIDGET_STYLE,
            ...(config?.styles?.widget as CSSProperties),
            height:
              config?.styles?.widget?.[currentBreakpoint]?.height ??
              config?.styles?.widget?.height,
            width:
              config?.styles?.widget?.[currentBreakpoint]?.width ??
              config?.styles?.widget?.width,
            zIndex: 3,
          }}
          role="application"
          onMouseEnter={() => {
            setHover(true);
            showOverlay();
            handleShowBubble();
            analytics().logEvent('widget_hover');
          }}
          onMouseLeave={() => {
            setHover(false);
            hideOverlay();
            handleHideBubble();
          }}
          // Only show the current time for the visualizer
          // and on the preview page
          data-current-time={
            window[HEY_GENIUS_FIELD]?.type === 'visualizer'
              ? mediaContext.current.currentTime
              : undefined
          }
        >
          {loading || !config ? (
            <div></div>
          ) : (
            <>
              {visible && config?.components?.controls?.enabled && (
                <div
                  id="controls"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    background:
                      'linear-gradient(to bottom, rgba(0, 0, 0, 0.2) 20%, rgba(0, 0, 0, 0.7) 100%)',
                    transition: 'opacity 0.5s ease-in-out',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'visible',
                    borderRadius: '50%',
                    zIndex: '100',
                    transform: 'translate(-50%, -50%)',
                    left: '50%',
                    top: '50%',
                    ...config?.styles?.widget_controls,
                  }}
                >
                  <Controls
                    toggleVideo={togglePlay}
                    playButtonText={
                      config?.components.play_button?.[config.language]
                    }
                  />
                </div>
              )}
              {progressBarEnabled && (
                <CircularMediaProgress
                  duration={
                    avatarRef.current?.media?.duration ??
                    avatarRef.current?.visualizer?.duration
                  }
                  color={config.colors?.primary}
                  size={
                    config?.styles?.widget?.[currentBreakpoint]?.height ??
                    config?.styles?.widget?.height ??
                    DEFAULT_WIDGET_STYLE.height
                  }
                  isPlaying={mediaContext?.current.isPlaying}
                  getCurrentTime={() => mediaContext?.current.currentTime}
                  onSeek={seekTo}
                />
              )}
              <div
                className={'widget-container'}
                ref={containerRef}
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'absolute',
                  // backdropFilter: 'blur(3px)',
                  zIndex: 21,
                  pointerEvents: 'all',
                  flexDirection: 'column',
                }}
              >
                <Avatar
                  mediaSrc={mediaSrc}
                  videoThumbnailUrl={config?.video_thumbnail_url}
                  type={window[HEY_GENIUS_FIELD]?.type ?? config.widget_type}
                  mediaStyle={{
                    width: '100%', // Scale the video to fit the container
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    ...dataRetrievalStrategy.getMediaStyle(),
                  }}
                  ref={avatarRefCallback}
                />
                <PlayButtonOverlay
                  isPlaying={isPlaying}
                  config={config}
                  currentBreakpoint={currentBreakpoint}
                  hover={hover}
                  playButtonColor={playButtonColor}
                />
                {window?.[HEY_GENIUS_FIELD]?.debug && (
                  <div className="debug">
                    {Math.round(displayedCurrentTime)}
                  </div>
                )}
                <a
                  rel="noopener noreferrer"
                  onClick={handlePoweredByClick}
                  className="powered-by"
                  data-testid="powered-by"
                  target="_blank"
                  href={config?.components?.powered_by_link}
                >
                  {config?.components?.powered_by?.[config.language]}{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {config?.components?.powered_by_brand}
                  </span>
                </a>
              </div>
              {config?.components?.bubble && (
                <Bubble
                  mobile={currentBreakpoint === 'small'}
                  show={showBubble}
                  config={config}
                />
              )}
              <Overlay
                id="overlay1"
                color={config?.colors?.gradient[0]}
                style={{ transform: 'translate(-20%, 10%)' }}
              />
              <Overlay
                id="overlay2"
                color={config?.colors?.gradient[1]}
                style={{ transform: 'translate(20%, 10%)' }}
              />
              <Overlay
                id="overlay3"
                color={config?.colors?.gradient[2]}
                style={{ transform: 'translateY(-20%)' }}
              />
            </>
          )}
        </div>
      </Draggable>
    </GlobalContextProvider>
  );
}
